import { ResearchActivityEvent, ResearchActivityPhase, ResearchActivityListener } from './ResearchActivityTypes';

let currentState: ResearchActivityEvent | null = null;
const listeners = new Set<ResearchActivityListener>();

function sanitizeErrorMessage(rawError?: string): string {
  if (!rawError) {
    return 'Live research took longer than expected. Please try again.';
  }
  const lower = rawError.toLowerCase();
  if (lower.includes('abort') || lower.includes('timeout') || lower.includes('signal is aborted')) {
    return 'Live research took longer than expected. Please try again.';
  }
  if (lower.includes('net::err') || lower.includes('fetch failed') || lower.includes('connection refusal')) {
    return 'Unable to reach live source right now. Please try again.';
  }
  // Remove technical call stacks or raw internal messages
  if (rawError.includes('Error:') || rawError.includes('at ') || rawError.includes('DOMException')) {
    return 'Live research query encountered an issue. Operating in fallback mode.';
  }
  return rawError;
}

export const ResearchActivityController = {
  getState(): ResearchActivityEvent | null {
    return currentState;
  },

  subscribe(listener: ResearchActivityListener): () => void {
    listeners.add(listener);
    listener(currentState); // Immediate initial emission
    return () => {
      listeners.delete(listener);
    };
  },

  broadcast(): void {
    const snapshot = currentState ? { ...currentState } : null;
    listeners.forEach((fn) => {
      try {
        fn(snapshot);
      } catch (err) {
        console.error('[ResearchActivityController] Error in listener callback:', err);
      }
    });
  },

  startResearch(query: string, requestId: string): ResearchActivityEvent {
    const now = Date.now();
    currentState = {
      requestId,
      phase: 'RESEARCH_REQUESTED',
      displayLabel: 'Research requested',
      startedAt: now,
      updatedAt: now,
    };
    this.broadcast();
    return currentState;
  },

  updatePhase(
    requestId: string,
    phase: ResearchActivityPhase,
    displayLabel: string,
    sourceName?: string
  ): boolean {
    if (!currentState || currentState.requestId !== requestId) {
      // Stale or invalid requestId — ignore to protect active turn state
      return false;
    }
    currentState = {
      ...currentState,
      phase,
      displayLabel,
      sourceName: sourceName || currentState.sourceName,
      updatedAt: Date.now(),
    };
    this.broadcast();
    return true;
  },

  completeResearch(requestId: string): boolean {
    if (!currentState || currentState.requestId !== requestId) {
      return false;
    }
    currentState = {
      ...currentState,
      phase: 'COMPLETE',
      displayLabel: 'Briefing ready',
      updatedAt: Date.now(),
    };
    this.broadcast();
    return true;
  },

  failResearch(requestId: string, rawError?: string): boolean {
    if (!currentState || currentState.requestId !== requestId) {
      return false;
    }
    const cleanError = sanitizeErrorMessage(rawError);
    currentState = {
      ...currentState,
      phase: 'FAILED',
      displayLabel: cleanError,
      error: cleanError,
      updatedAt: Date.now(),
    };
    this.broadcast();
    return true;
  },

  resetState(): void {
    currentState = null;
    this.broadcast();
  },
};
